import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Activity, Brain, Zap, AlertTriangle, CheckCircle2, TrendingUp, Watch } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = localStorage.getItem('annalise_session_id');
  if (!id) {
    id = `annalise_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('annalise_session_id', id);
  }
  return id;
};

const priorityIcon = (type, priority) => {
  if (type === 'weekly_summary') return <TrendingUp className="w-5 h-5 text-[#166534]" />;
  if (type === 'device_sync') return <Watch className="w-5 h-5 text-blue-500" />;
  if (type === 'trend') return <Activity className="w-5 h-5 text-purple-500" />;
  if (priority === 'high') return <AlertTriangle className="w-5 h-5 text-red-500" />;
  if (priority === 'medium') return <Brain className="w-5 h-5 text-amber-500" />;
  return <CheckCircle2 className="w-5 h-5 text-green-500" />;
};

const priorityBg = (type, priority) => {
  if (type === 'weekly_summary') return 'bg-green-50 border-green-100';
  if (type === 'device_sync') return 'bg-blue-50 border-blue-100';
  if (priority === 'high') return 'bg-red-50 border-red-100';
  if (priority === 'medium') return 'bg-amber-50 border-amber-100';
  return 'bg-gray-50 border-gray-100';
};

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/notifications/${getSessionId()}`);
      const data = await res.json();
      setNotifications(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const handler = () => fetchNotifications();
    window.addEventListener('healthDataUpdated', handler);
    return () => window.removeEventListener('healthDataUpdated', handler);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="outline" size="sm" className="gap-2 relative"
        onClick={() => { setOpen(!open); if (!open) fetchNotifications(); }}
        data-testid="notification-bell-btn"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold" data-testid="notification-badge">
            {unread}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-[#166534] to-[#22c55e]">
            <h3 className="font-semibold text-white text-sm">Notifications</h3>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white"><X className="w-4 h-4" /></button>
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-400 text-sm">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">No notifications yet. Log health data to get started!</div>
            ) : (
              <div className="p-2 space-y-2">
                {notifications.map(n => (
                  <div key={n.id} className={`p-3 rounded-xl border ${priorityBg(n.type, n.priority)} transition-all hover:shadow-sm`} data-testid={`notification-${n.type}`}>
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">{priorityIcon(n.type, n.priority)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{n.title}</p>
                        <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{n.message}</p>
                        <span className="text-[10px] text-gray-400 mt-1 block">{n.time}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-gray-100 bg-gray-50">
            <Button size="sm" variant="ghost" className="w-full text-[#166534] text-xs" onClick={() => { setOpen(false); window.dispatchEvent(new CustomEvent('openHealthForm')); }}>
              Log Health Data for Better Insights
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
